import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Target, Heart, Lightbulb, HandshakeIcon, Award, Users, TrendingUp, Linkedin } from 'lucide-react';
const values = [{
  icon: Heart,
  title: 'People First',
  description: 'Behind every claim is a person and a family. We never lose sight of that.'
}, {
  icon: Target,
  title: 'Outcomes-Driven',
  description: 'We measure success by results — both clinical recovery and financial savings.'
}, {
  icon: Lightbulb,
  title: 'Continuously Evolving',
  description: 'We invest in technology and training to stay ahead of an ever-changing industry.'
}, {
  icon: HandshakeIcon,
  title: 'Partnership Mindset',
  description: 'We win when our clients win. Long-term relationships are at the core of our business.'
}];
const leadership = [{
  name: 'Sarah Mitchell',
  role: 'Chief Executive Officer',
  bio: "25+ years in workers' compensation. Former VP at a top-5 national carrier.",
  image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90'
}, {
  name: 'Dr. James Patel',
  role: 'Chief Medical Officer',
  bio: 'Board-certified physician with 20+ years of occupational medicine experience.',
  image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90'
}, {
  name: 'Maria Gonzalez',
  role: 'VP of Operations',
  bio: 'Operations leader specializing in scaling national managed care networks.',
  image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90'
}, {
  name: 'Robert Chen',
  role: 'Chief Technology Officer',
  bio: 'Builds the data and AI infrastructure that powers our managed care platform.',
  image: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90'
}];
export function About() {
  return <div className="bg-brand-paper" data-id="element-369">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-370">
        <div className="absolute inset-0 opacity-10" data-id="element-371">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-372"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-373">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-374">
            <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6" data-id="element-375">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-376">
                About Arissa
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-377">
              We help employers{' '}
              <span className="text-brand-gold italic" data-id="element-378">navigate care</span> for
              their injured workers.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed font-light max-w-2xl" data-id="element-379">
              For over 25 years, Arissa Cost Solutions has been a trusted
              national partner to insurance carriers, TPAs, and employers —
              combining intelligent technology with deeply personal service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission / Story */}
      <section className="py-24 bg-white" data-id="element-380">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-381">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-382">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-383">
              Our Story
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-384">
              Built on a simple belief: care matters.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-5" data-id="element-385">
              Arissa Cost Solutions was founded on the conviction that workers'
              compensation managed care could be done better — with more
              humanity, more technology, and more accountability.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-5" data-id="element-386">
              Today we provide highly integrated and comprehensive managed care
              services for workers' compensation and liability claims through
              our exceptional team, customized programs, and proprietary
              technology platform.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-387">
              Our tagline says it all:{' '}
              <span className="font-semibold text-brand-navy italic" data-id="element-388">
                Evolve. Grow. Align.
              </span>{' '}
              We help our partners modernize their claims approach, scale their
              capabilities, and align medical management with measurable
              outcomes.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-389">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform -rotate-2" data-id="element-390"></div>
            <img src="https://images.unsplash.com/photo-1609220136736-443140cffec6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Older couple together, representing the families we serve" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-391" />
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-392">
        <div className="absolute inset-0 opacity-5" data-id="element-393">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} data-id="element-394"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-10 relative z-10" data-id="element-395">
          {[{
          stat: '25+',
          label: 'Years in business',
          icon: Award
        }, {
          stat: '500K+',
          label: 'Claims managed',
          icon: TrendingUp
        }, {
          stat: '1.2M+',
          label: 'Network providers',
          icon: Users
        }, {
          stat: '50',
          label: 'States served',
          icon: Target
        }].map(s => {
          const Icon = s.icon;
          return <div key={s.label} className="text-center text-white" data-id="element-396">
                <Icon className="w-7 h-7 text-brand-gold mx-auto mb-4" data-id="element-397" />
                <div className="text-4xl md:text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-398">
                  {s.stat}
                </div>
                <div className="text-sm uppercase tracking-wider text-white/80 font-medium" data-id="element-399">
                  {s.label}
                </div>
              </div>;
        })}
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-brand-paper" data-id="element-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-401">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-402">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-403">
              What We Stand For
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-404">
              Our values guide every decision.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-405">
            {values.map((v, i) => {
            const Icon = v.icon;
            return <motion.div key={v.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-xl transition-shadow border border-gray-100" data-id="element-406">
                  <div className="w-14 h-14 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mb-6" data-id="element-407">
                    <Icon className="w-7 h-7" data-id="element-408" />
                  </div>
                  <h4 className="font-serif font-bold text-xl text-brand-navy mb-3" data-id="element-409">
                    {v.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed" data-id="element-410">
                    {v.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-24 bg-white" data-id="element-411">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-412">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-413">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-414">
              Leadership
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-415">
              Meet the team behind Arissa.
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-416">
            {leadership.map((person, i) => <motion.div key={person.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="group" data-id="element-417">
                <div className="relative rounded-xl overflow-hidden mb-5 aspect-[3/4]" data-id="element-418">
                  <img src={person.image} alt={person.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-419" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-transparent" data-id="element-420"></div>
                  <a href="#" className="absolute bottom-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-brand-gold hover:text-brand-navy transition-colors" data-id="element-421">
                    <Linkedin className="w-4 h-4" data-id="element-422" />
                  </a>
                </div>
                <h4 className="font-serif font-bold text-xl text-brand-navy mb-1" data-id="element-423">
                  {person.name}
                </h4>
                <div className="text-brand-gold text-sm font-semibold mb-3 uppercase tracking-wide" data-id="element-424">
                  {person.role}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-425">
                  {person.bio}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-426">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-427"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-428">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-429">
            Let's build a stronger program together.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-430">
            Connect with our team to learn how Arissa Cost Solutions can
            transform your workers' compensation outcomes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-431">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto flex items-center justify-center" data-id="element-432">
              Get in Touch <ArrowRight className="w-5 h-5 ml-2" data-id="element-433" />
            </Link>
            <a href="tel:8552601053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-434">
              <Phone className="w-5 h-5 mr-2" data-id="element-435" /> 855.260.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}